import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IFUIcon } from "../icons/IFUIcon";

export default function BottomNavBar({
  className = "",
  menuOpen,
  onToggleMenu,
  onCloseMenu,
}) {
  const { t, i18n } = useTranslation();

  const lang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en-GB"
  ).toLowerCase();
  const ifuHref = `https://www.electronrx.com/instructions-for-use-addenbrookes`;

  const items = [
    {
      to: "/home",
      iconType: "material",
      icon: "home",
      label: t("navBar.home", "Home"),
    },
    {
      href: ifuHref,
      iconType: "jsx",
      icon: <IFUIcon />,
      label: t("navBar.ifu_short", "Instructions"),
      external: true,
    },
    {
      to: "/results",
      iconType: "material",
      icon: "pending_actions",
      label: t("navBar.results", "Results"),
    },

    {
      action: "menu",
      iconType: "material",
      icon: "menu",
      label: t("navBar.menu", "Menu"),
    },
  ];

  const base = "flex flex-col items-center justify-center gap-1 py-2 min-w-0";
  const textBase = "text-[11px] leading-none";

  // inside BottomNavBar component

  const renderIcon = (item, iconOverride) => {
    const iconToRender = iconOverride ?? item.icon;

    if (item.iconType === "jsx") {
      return (
        <span
          className="h-6 w-auto flex items-center justify-center text-current
                   [&_svg]:w-full [&_svg]:h-full [&_svg]:fill-current [&_svg]:stroke-current"
          aria-hidden="true"
        >
          {iconToRender}
        </span>
      );
    }

    return (
      <span
        className="material-symbols-outlined text-[24px]"
        aria-hidden="true"
      >
        {iconToRender}
      </span>
    );
  };

  return (
    <nav
      className={`md:hidden h-fit z-50 bg-container ${!menuOpen && "border-t border-greyscale-3"} ${className}`}
      aria-label="Bottom navigation"
    >
      <ul className="grid grid-cols-4 h-fit">
        {items.map((item) => {
          const key = item.to ?? item.href ?? item.action;

          if (item.action === "menu") {
            return (
              <li key={key} className="contents">
                <button
                  type="button"
                  onClick={onToggleMenu}
                  className={[
                    base,
                    menuOpen ? "text-primary" : "text-typography",
                  ].join(" ")}
                  aria-label={item.label}
                  aria-expanded={menuOpen}
                >
                  {renderIcon(item, menuOpen ? "menu_open" : "menu")}
                  <span className={textBase}>{item.label}</span>
                </button>
              </li>
            );
          }

          // External link (IFU)
          if (item.external) {
            return (
              <li key={key} className="contents">
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onCloseMenu}
                  className={[base, "text-typography"].join(" ")}
                  aria-label={item.ariaLabel ?? item.label}
                >
                  {renderIcon(item)}
                  <span className={textBase}>{item.label}</span>
                </a>
              </li>
            );
          }

          // Internal nav
          return (
            <li key={key} className="contents">
              <NavLink
                to={item.to}
                onClick={onCloseMenu}
                className={({ isActive }) =>
                  [base, isActive ? "text-primary" : "text-typography"].join(
                    " ",
                  )
                }
                aria-label={item.label}
              >
                {renderIcon(item)}
                <span className={textBase}>{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
